import { BestInDentalV2 } from '~/constants/ff.posthog';
import { useBidWinnerRecognition } from '~/domain/BestInDental';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';

interface IBestInDentalWinnerCredential {
  isWinner: boolean;
  /** The winning year when known; a winner without one still shows the credential. */
  year: number | undefined;
}

/**
 * Whether the office the app is scoped to has won Best in Dental, for the
 * credential the account area shows.
 *
 * Flag ON: awards-service (the same `useBidWinnerRecognition` source as the
 * dashboard `BidWinnerBanner`). Flag OFF: the legacy `/auth/me` winner blob, so
 * offices that already won keep the credential through the two-phase rollout —
 * losing it until the flag flips would be a regression.
 */
export const useBestInDentalWinnerCredential = (): IBestInDentalWinnerCredential => {
  const isFlagOn = useFeatureFlag(BestInDentalV2);
  const { isEverWinner, currentCycleWin, isLoading } = useBidWinnerRecognition(isFlagOn);
  const currentUser = useUserSelector();

  const legacyBid = currentUser?.office_info?.bestInDental;
  const isWinner = isFlagOn ? !isLoading && isEverWinner : legacyBid?.status === 'winner';
  const year = isFlagOn ? currentCycleWin?.cycleYear : legacyBid?.winnerDetails?.[0]?.winningYear;

  return { isWinner: !!isWinner, year: year != null ? Number(year) : undefined };
};
